/*
 * Shared pieces of the health-scores screen: tier colours, the category band
 * bar, the tier word, the points badge, the range ladder (one row of the
 * framework's point tables) and the sample report card (the default case).
 * The live five-step section is in ScoreSimulator.jsx.
 */
import ReportCover from '../../mocks/ReportCover';
import ScoreRing from '../../mocks/ScoreRing';
import { AlertGlyph, PinDropGlyph } from '../../mocks/ProductGlyphs';
import { TIERS, computeScore, tierFor } from './scoreModel';
import './HealthScoreParts.css';

/* Fill colour (shapes) and ink (text on white, 4.5:1+) for each category. */
export const TIER_TONE = {
  Poor: { fill: '#e5484d', ink: '#b42318' },
  Suboptimal: { fill: '#f5841f', ink: '#b54708', word: '#e06c00' }, // word: bright orange, 3.3:1 for the large tier word
  Fair: { fill: '#f2b01f', ink: '#8a6100' },
  Good: { fill: '#1fa463', ink: '#157a49' },
  Optimal: { fill: '#13a8a8', ink: '#0b7a7a' },
};

/* Colour for a points value: protective (negative) teal, 0 green, then up to red at 4. */
const PTS_FILL = {
  '-2': '#13a8a8',
  '-1': '#13a8a8',
  0: '#1fa463',
  1: '#f2b01f',
  2: '#f5841f',
  3: '#ec6a3c',
  4: '#e5484d',
};
export const ptsFill = (pts) => PTS_FILL[pts] ?? '#e5484d';
export const signed = (n) => (n > 0 ? `+${n}` : `${n}`);
export const one = (n) => (Math.round(n * 10) / 10).toFixed(1);

/* The demo's starting state: three findings on; prediabetes, lifestyle and method off. */
export const DEFAULT_FINDINGS = {
  iron: true,
  vitD: true,
  esr: true,
  prediabetes: false,
  active: false,
  smoker: false,
  plainAverage: false,
};
export const DEFAULT_RESULT = computeScore(DEFAULT_FINDINGS);

/* Where the pin sits: five equal segments, placed within its band (Poor drawn as 51–100). */
function markerLeft(score) {
  const s = Math.round(score);
  const i = TIERS.indexOf(tierFor(s));
  const lo = i === 0 ? 0 : TIERS[i].lo;
  const hi = i === TIERS.length - 1 ? 100 : TIERS[i].hi;
  const within = Math.min(1, Math.max(0, (s - lo + 0.5) / (hi - lo + 1)));
  return `${((i + within) / TIERS.length) * 100}%`;
}

/* The framework's five categories, best first, with a pin at the score. */
export function BandBar({ score, labels = true, className = '' }) {
  const current = tierFor(score);
  return (
    <div className={`hs-bands ${className}`}>
      <div className="hs-bands__track" aria-hidden="true">
        {TIERS.map((b) => (
          <span
            key={b.tier}
            className={b === current ? 'is-current' : undefined}
            style={{ background: TIER_TONE[b.tier].fill }}
          />
        ))}
        <span className="hs-bands__pin" style={{ left: markerLeft(score), '--pin': TIER_TONE[current.tier].fill }} />
      </div>
      {labels ? (
        <ol className="hs-bands__labels" aria-label={`Categories; ${Math.round(score)} is ${current.tier}`}>
          {TIERS.map((b) => (
            <li key={b.tier} className={b === current ? 'is-current' : undefined}>
              <span>{b.range}</span>
              <span>{b.tier}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="sr-only">{`Score ${Math.round(score)} is in the ${current.tier} category.`}</p>
      )}
    </div>
  );
}

export function TierWord({ tier }) {
  return (
    <p className="hs-tierline">
      <span className="hs-tier" style={{ color: TIER_TONE[tier].word ?? TIER_TONE[tier].ink }}>
        {tier}
      </span>
    </p>
  );
}

export function PtsBadge({ pts, className = '' }) {
  return (
    <span className={`hs-pts ${className}`} style={{ '--pts': ptsFill(pts) }}>
      {signed(pts)}
      <span className="sr-only"> points</span>
    </span>
  );
}

/*
 * One row of a point table: the ranges in value order, each with its points,
 * the patient's range lit. Hatched ranges are ones the framework gives no
 * points for.
 */
export function Ladder({ name, unit, value, segments, index, pts }) {
  const on = segments[index];
  return (
    <li className="hs-ladder">
      <span className="hs-ladder__name">
        {name}
        {unit && <small>{unit}</small>}
      </span>
      <span className="hs-ladder__track" aria-hidden="true">
        {segments.map((s, i) => (
          <span
            key={s.label}
            className={`${i === index ? 'is-on' : ''}${s.uncovered ? ' is-gap' : ''}`}
            style={{ '--pts': ptsFill(s.pts) }}
          >
            <b>{s.uncovered ? '–' : signed(s.pts)}</b>
            <small>{s.label}</small>
          </span>
        ))}
      </span>
      <span className="hs-ladder__value">{value}</span>
      <PtsBadge pts={pts} />
      <span className="sr-only">
        {on.uncovered ? ', a range the framework does not score.' : `, in range ${on.label}.`}
      </span>
    </li>
  );
}

const KEY_FINDINGS = [
  { key: 'iron', name: 'Iron-deficiency anaemia', tone: 'high' },
  { key: 'vitd', name: 'Vitamin D deficiency', tone: 'borderline' },
  { key: 'esr', name: 'Raised ESR', tone: 'esr' },
];

function FindingMark({ tone }) {
  if (tone === 'esr') return <PinDropGlyph size={16} color="#f2a91f" />;
  return <AlertGlyph size={16} color={tone === 'high' ? '#e5484d' : '#f2b01f'} />;
}

/*
 * The sample report: a static picture of the default case, drawn as in the
 * mockup: a tilted report page over a "Key findings" sheet and a third,
 * blurred page behind it.
 */
export function ReportCard() {
  const r = DEFAULT_RESULT;
  const tone = TIER_TONE[r.tier];
  return (
    <div className="hs-report">
      <ReportCover className="hs-report__cover" date="Test Date: 12 Oct 2024">
        <div className="hs-report__score">
          <ScoreRing value={r.score} label="Wellness Score" color={tone.fill} size={94} className="hs-ring" />
          <div>
            <TierWord tier={r.tier} />
            <p className="hs-report__why">{r.band.meaning}</p>
            <p className="hs-lower">Lower is better</p>
          </div>
        </div>
        <BandBar score={r.score} />
      </ReportCover>
      <span className="hs-report__paper" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </span>
      <aside className="hs-report__sheet" aria-label="Key findings on the sample report">
        <span className="hs-report__sheet-rule" aria-hidden="true" />
        <p className="hs-report__sheet-title">Key findings</p>
        <ul>
          {KEY_FINDINGS.map((f) => (
            <li key={f.key} className={`hs-finding hs-finding--${f.tone}`}>
              <FindingMark tone={f.tone} />
              {f.name}
            </li>
          ))}
        </ul>
        <p className="hs-report__sheet-title">
          Other systems<span className="sr-only">: all in range</span>
        </p>
        <span className="hs-report__others" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </aside>
    </div>
  );
}
